import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/auth-context';
import { useSite } from '@/contexts/SiteContext';
import { API_URL } from '@/config/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Site {
  id?: string;
  _id?: string;
  name: string;
  url: string;
  status: string;
  type?: string;
  sector_id?: number;
  sector_ids?: number[];
}
interface Customer { id: string; name: string; }

export const SiteSelector: React.FC = () => {
  const { user } = useAuth();
  const { selectedSite, setSelectedSite, sites, setSites, setLoading } = useSite();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    if (!user) return;
    
    const authToken = localStorage.getItem('auth_token');
    if (!authToken) {
      console.error('No auth token found in localStorage');
      return;
    }
    
    setLoading(true);
    // Always fetch the user's sites first
    axios.get(`${API_URL.replace(/\/$/, '')}/users/me/sites`, {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    })
      .then(res => {
        console.log('Sites API response:', res.data);
        // The API returns { sites: [...], isSuperAdmin: boolean, totalCount: number }
        const fetchedSites = res.data.sites || [];
        setSites(fetchedSites);
        setIsSuperAdmin(res.data.isSuperAdmin || false);
        
        // Select first active site if none selected
        if (!selectedSite && fetchedSites.length > 0) {
          const activeSites = fetchedSites.filter((site: Site) => site && site.status === 'active');
          if (activeSites.length > 0) {
            setSelectedSite(activeSites[0]);
          }
        }
        
        // If super admin, also fetch customers
        if (res.data.isSuperAdmin) {
          axios.get(`${API_URL}/customers`, {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          }).then(custRes => setCustomers(custRes.data)).catch(() => {
            // If customers endpoint doesn't exist, that's okay
            console.log('Customers endpoint not available');
          });
        }
      })
      .catch(err => {
        console.error('Failed to fetch sites:', err.response?.data || err.message);
        setSites([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user, setSites, setSelectedSite, setLoading]);

  useEffect(() => {
    if (isSuperAdmin && customerId) {
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) return;
      
      axios.get(`${API_URL}/customers/${customerId}/sites`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      })
        .then(res => {
          const sites = Array.isArray(res.data) ? res.data : (res.data?.sites || []);
          setSites(sites.filter(site => site != null));
        })
        .catch(() => {
          console.log('Customer sites endpoint not available');
        });
    }
  }, [isSuperAdmin, customerId, setSites]);

  if (!user) return null;

  const handleSiteChange = (siteId: string) => {
    const site = sites.find(s => (s._id || s.id) === siteId);
    setSelectedSite(site || null);
  };

  const handleCustomerChange = (customerId: string) => {
    setCustomerId(customerId);
  };

  return (
    <div className="flex gap-2">
      {isSuperAdmin && customers.length > 0 && (
        <Select value={customerId} onValueChange={handleCustomerChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select customer" />
          </SelectTrigger>
          <SelectContent>
            {customers.map(c => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Select 
        value={selectedSite?._id || selectedSite?.id || ''} 
        onValueChange={handleSiteChange}
      >
        <SelectTrigger className="w-[500px] max-w-[500px] [&>span]:truncate [&>span]:block">
          <SelectValue placeholder="Select site" />
        </SelectTrigger>
        <SelectContent className="max-w-[550px]">
          {sites.map(s => (
            <SelectItem key={s._id || s.id} value={s._id || s.id || ''}>
              <span className="truncate block max-w-[480px]">{s.name || s.url}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
